import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { absenceService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTeam } from '../context/TeamContext';

const AbsencesContext = createContext(null);

export const useAbsences = () => {
  const context = useContext(AbsencesContext);
  if (!context) {
    throw new Error('useAbsences must be used within an AbsencesProvider');
  }
  return context;
};

export const AbsencesProvider = ({ children }) => {
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();
  const { selectedTeam } = useTeam();
  const [initialized, setInitialized] = useState(false);
  
  // Flag pour éviter les appels multiples
  const isLoadingRef = useRef(false);

  // Charger toutes les absences
  const loadAbsences = useCallback(async (forceReload = false, teamOverride = null) => {
    // Si déjà en cours de chargement, ne pas relancer
    if (isLoadingRef.current && !forceReload) {
      console.log('⏳ AbsencesContext - Chargement déjà en cours, abandon');
      return;
    }
    
    // Si pas d'utilisateur connecté, nettoyer les données
    if (!currentUser?.id) {
      console.log('👤 AbsencesContext - Pas d\'utilisateur connecté, nettoyage des données');
      setAbsences([]);
      setError(null);
      setLoading(false);
      return;
    }

    try {
      isLoadingRef.current = true;
      setLoading(true);
      setError(null);

      const teamToUse = teamOverride || selectedTeam;
      console.log('🎯 AbsencesContext - Chargement des absences pour:', currentUser?.email, 'équipe:', teamToUse?.name);

      // Chargement limité par défaut (6 derniers mois) pour améliorer les performances
      const data = await absenceService.getAll(teamToUse?.id, { limitToRecentMonths: 6 });
      setAbsences(data);
      setInitialized(true);
    } catch (err) {
      console.error('❌ AbsencesContext - Erreur:', err);
      setError(err.message);
      setAbsences([]);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [currentUser?.id, currentUser?.email, selectedTeam?.id]);

  // Nettoyer les données quand l'utilisateur se déconnecte
  useEffect(() => {
    if (!currentUser?.id) {
      setAbsences([]);
      setError(null);
      setInitialized(false);
    }
  }, [currentUser?.id]);

  // Écouter les changements d'équipe
  useEffect(() => {
    const handleTeamChange = (event) => {
      const { team, userId } = event.detail;
      
      if (userId === currentUser?.id) {
        console.log('🔄 AbsencesContext - Changement d\'équipe détecté:', team.name);
        loadAbsences(true, team); // Force le rechargement avec la nouvelle équipe
      }
    };

    window.addEventListener('teamChanged', handleTeamChange);
    
    return () => {
      window.removeEventListener('teamChanged', handleTeamChange);
    };
  }, [currentUser?.id, loadAbsences]);

  const addAbsence = useCallback(async (absenceData) => {
    try {
      console.log('➕ Ajout absence:', absenceData);
      console.log('🎯 Équipe sélectionnée pour création absence:', selectedTeam);
      
      if (!selectedTeam || !selectedTeam.id) {
        throw new Error('Aucune équipe sélectionnée pour créer l\'absence');
      }
      
      const absenceDataWithTeam = {
        ...absenceData,
        teamId: selectedTeam.id
      };
      
      const newAbsence = await absenceService.create(absenceDataWithTeam);
      if (currentUser?.id && initialized) {
        await loadAbsences(true);
      }
      console.log('✅ Absence ajoutée:', newAbsence);
      return newAbsence;
    } catch (err) {
      console.error('❌ Erreur lors de l\'ajout:', err);
      setError('Erreur lors de l\'ajout');
      throw err;
    }
  }, [currentUser?.id, initialized, loadAbsences, selectedTeam]);

  const updateAbsence = useCallback(async (id, absenceData) => {
    try {
      console.log('✏️ Mise à jour absence:', id, absenceData);
      
      if (!selectedTeam || !selectedTeam.id) {
        throw new Error('Aucune équipe sélectionnée pour modifier l\'absence');
      }
      
      const absenceDataWithTeam = {
        ...absenceData,
        teamId: selectedTeam.id
      };
      
      const updatedAbsence = await absenceService.update(id, absenceDataWithTeam);
      if (currentUser?.id && initialized) {
        await loadAbsences(true);
      }
      console.log('✅ Absence mise à jour:', updatedAbsence);
      return updatedAbsence;
    } catch (err) {
      console.error('❌ Erreur lors de la mise à jour:', err);
      setError('Erreur lors de la mise à jour');
      throw err;
    }
  }, [currentUser?.id, initialized, loadAbsences, selectedTeam]);

  const deleteAbsence = useCallback(async (id) => {
    try {
      await absenceService.delete(id);
      if (currentUser?.id && initialized) {
        await loadAbsences(true);
      }
    } catch (err) {
      console.error('❌ Erreur lors de la suppression:', err);
      setError('Erreur lors de la suppression');
      throw err;
    }
  }, [currentUser?.id, initialized, loadAbsences, selectedTeam]);

  // Synchronisation intelligente Liisa :
  // - crée les absences présentes dans le fichier mais absentes en base
  // - met à jour celles dont le type a changé
  // - supprime les absences "Import Liisa" de l'équipe absentes du fichier
  // - ne touche pas aux absences créées manuellement
  // - toutes les opérations DB en un seul appel API pour éviter le rate-limiting
  const syncLiisaAbsences = useCallback(async (absencesToSync, onProgress) => {
    if (!selectedTeam || !selectedTeam.id) {
      throw new Error('Aucune équipe sélectionnée');
    }

    const report = onProgress || (() => {});

    report({ percent: 5, label: 'Chargement des données existantes…' });

    // Charger TOUTES les absences existantes (sans limite de date) pour comparaison complète
    const allExisting = await absenceService.getAll(selectedTeam.id, { fullLoad: true });

    // Ne considérer que les absences issues d'un import Liisa pour la suppression
    const existingLiisa = allExisting.filter(a =>
      a.comment && a.comment.startsWith('Import Liisa')
    );

    report({ percent: 20, label: 'Analyse des différences…' });

    const key = (a) => `${a.technicianId}|${a.startDate}|${a.endDate}`;
    const existingByKey = new Map(existingLiisa.map(a => [key(a), a]));
    const fileKeys = new Set(absencesToSync.map(key));

    const toCreate = [];
    const toUpdate = [];
    for (const fileAbs of absencesToSync) {
      const existing = existingByKey.get(key(fileAbs));
      if (!existing) {
        toCreate.push({ ...fileAbs, teamId: selectedTeam.id });
      } else if (existing.type !== fileAbs.type) {
        toUpdate.push({ id: existing.id, ...fileAbs, teamId: selectedTeam.id });
      }
    }
    const toDelete = existingLiisa
      .filter(a => !fileKeys.has(key(a)))
      .map(a => a.id);

    report({
      percent: 40,
      label: `Envoi des modifications… (+${toCreate.length} ~${toUpdate.length} -${toDelete.length})`,
    });

    // Un seul appel API pour toutes les opérations
    await absenceService.bulkSync({ toCreate, toUpdate, toDelete });

    report({ percent: 90, label: 'Rechargement des données…' });

    if (currentUser?.id && initialized) {
      await loadAbsences(true);
    }

    report({ percent: 100, label: 'Terminé !' });

    return {
      created:   toCreate.length,
      updated:   toUpdate.length,
      deleted:   toDelete.length,
      unchanged: absencesToSync.length - toCreate.length - toUpdate.length,
    };
  }, [currentUser?.id, initialized, loadAbsences, selectedTeam]);

  // Création en masse simple (sans sync) : envoie toutes les requêtes en parallèle.
  const addAbsencesBatch = useCallback(async (absencesData) => {
    if (!selectedTeam || !selectedTeam.id) {
      throw new Error('Aucune équipe sélectionnée pour créer les absences');
    }

    const results = await Promise.allSettled(
      absencesData.map(absenceData =>
        absenceService.create({ ...absenceData, teamId: selectedTeam.id })
      )
    );

    if (currentUser?.id && initialized) {
      await loadAbsences(true);
    }

    return {
      successes: results.filter(r => r.status === 'fulfilled').length,
      failures:  results.filter(r => r.status === 'rejected').length,
    };
  }, [currentUser?.id, initialized, loadAbsences, selectedTeam]);

  // Fonction d'initialisation manuelle
  const initializeData = useCallback(async (userOverride = null) => {
    if (!userOverride?.id && !currentUser?.id) return;
    await loadAbsences(true);
  }, [loadAbsences, currentUser]);

  // Fonction pour charger toutes les absences (pour planification)
  const loadAllAbsences = useCallback(async () => {
    if (!currentUser?.id) return;
    
    const teamToUse = selectedTeam;
    console.log('📅 AbsencesContext - Chargement COMPLET des absences pour planification:', teamToUse?.name);
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await absenceService.getAll(teamToUse?.id, { fullLoad: true });
      setAbsences(data);
      console.log('✅ AbsencesContext - Toutes les absences chargées:', data.length);
    } catch (err) {
      console.error('❌ AbsencesContext - Erreur lors du chargement complet:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id, selectedTeam]);

  const value = {
      absences,
      loading,
      error,
    initialized,
      addAbsence,
      addAbsencesBatch,
      syncLiisaAbsences,
      updateAbsence,
    deleteAbsence,
    initializeData,
    loadAbsences,
    loadAllAbsences
  };

  return (
    <AbsencesContext.Provider value={value}>
      {children}
    </AbsencesContext.Provider>
  );
}; 

export default AbsencesProvider; 